import React from 'react';
import { narrativeDelta } from '../../lib/uiHelpers';

/**
 * A1. KPI card with an auto-computed narrative "why it changed"
 * annotation. Pass current + previous values; the card renders the
 * headline number, a small delta pill, and a one-sentence explanation.
 *
 *   <NarrativeKPI
 *     label="Active Platforms"
 *     value={87}
 *     previous={82}
 *     hint="Coastal Beach added Cloudbeds this week"
 *   />
 */
export default function NarrativeKPI({
  label,
  value,
  previous,
  unit = '',
  hint,               // optional agent/audit-log explanation
  formatValue,
  invertColor = false, // when "up" is bad (e.g. anomalies)
  icon: Icon,
  className = '',
  testId,
}) {
  const delta = narrativeDelta(value, previous, { invertColor, unit, formatValue });
  const displayValue = formatValue ? formatValue(value) : `${(value ?? 0).toLocaleString()}${unit}`;

  const toneClass = delta?.tone === 'positive'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
    : delta?.tone === 'negative'
      ? 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900'
      : 'bg-muted text-muted-foreground border-border';

  return (
    <div
      className={`rounded-xl border bg-card p-4 flex flex-col gap-2 ${className}`}
      data-testid={testId || `narrative-kpi-${label?.toLowerCase()?.replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
          {displayValue}
        </span>
        {delta && delta.direction !== 'flat' && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold tabular-nums ${toneClass}`}
            data-testid="narrative-kpi-delta-pill"
          >
            {delta.direction === 'up' ? '↑' : '↓'} {delta.pct != null ? `${delta.pct > 0 ? '+' : ''}${delta.pct.toFixed(delta.pct >= 10 || delta.pct <= -10 ? 0 : 1)}%` : Math.abs(delta.diff)}
          </span>
        )}
      </div>
      {(hint || delta) && (
        <div className="text-[11px] text-muted-foreground leading-snug" data-testid="narrative-kpi-hint">
          {hint || delta?.sentence}
        </div>
      )}
    </div>
  );
}
